import { buildMongoQueryMatcher, createMongoAbility, RawRuleOf } from '@casl/ability';
import { $and, $or, and, or } from '@ucast/mongo2js';

import {
  AbilityConditionPropertiesBySubject,
  AbilityConditionProperty,
  AbilityConditionPropertyEnum,
  AuthAbilityRule,
  SlnkMongoAbility,
} from '@app/models/api/v1/auth/ability.model';

const ABILITY_CONDITION_PROPERTIES = Object.values(AbilityConditionPropertyEnum);

const conditionsMatcher = buildMongoQueryMatcher({ $and, $or }, { and, or });

export function createSlnkAbility(rules: RawRuleOf<SlnkMongoAbility>[] = []): SlnkMongoAbility {
  return createMongoAbility<SlnkMongoAbility>(rules, { conditionsMatcher });
}

export function packApiAbilityRules(rules: AuthAbilityRule[]): RawRuleOf<SlnkMongoAbility>[] {
  return rules.map(rule => {
    const packed: RawRuleOf<SlnkMongoAbility> = {
      action: rule.action,
      subject: rule.subject,
    };

    if (rule.conditions !== undefined) {
      packed.conditions = rule.conditions;
    }

    if (rule.fields !== undefined && rule.fields.length > 0) {
      packed.fields = rule.fields;
    }

    if (rule.inverted) {
      packed.inverted = true;
    }

    return packed;
  });
}

export function mapAbilityConditionProperties(rules: readonly RawRuleOf<SlnkMongoAbility>[]): AbilityConditionPropertiesBySubject {
  return rules
    .flatMap(rule => {
      const subjects = Array.isArray(rule.subject) ? rule.subject : [rule.subject];
      const actions = Array.isArray(rule.action) ? rule.action : [rule.action];
      const properties = getAbilityConditionProperties(rule.conditions);

      return subjects.flatMap(subject => actions.map(action => ({ subject, action, properties })));
    })
    .reduce<AbilityConditionPropertiesBySubject>((grouped, { subject, action, properties }) => {
      const combined = [...(grouped[subject]?.[action] ?? []), ...properties];

      return {
        ...grouped,
        [subject]: {
          ...grouped[subject],
          [action]: ABILITY_CONDITION_PROPERTIES.filter(property => combined.includes(property)),
        },
      };
    }, {});
}

function getAbilityConditionProperties(node: unknown): AbilityConditionProperty[] {
  if (Array.isArray(node)) {
    return node.flatMap(item => getAbilityConditionProperties(item));
  }

  if (node === null || typeof node !== 'object') {
    return [];
  }

  return Object.entries(node).flatMap(([key, value]): AbilityConditionProperty[] => {
    if (key.startsWith('$')) {
      return getAbilityConditionProperties(value);
    }

    const property = ABILITY_CONDITION_PROPERTIES.find(candidate => candidate === key);
    return property === undefined ? [] : [property];
  });
}
